import { dayPlus, daysApart } from "@bl/domain";
import { useNavigate } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Flame,
  Repeat as RepeatIcon,
  TriangleAlert,
} from "lucide-react";
import {
  type CSSProperties,
  type MouseEvent,
  type PointerEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { send, type Tank, useChange, useTankStays } from "../api";
import { useMedia } from "../components/DocumentViewer";
import { ErrorNote } from "../components/ui";
import { ZoomSlider } from "../components/ZoomSlider";
import { showDate, today } from "../format";
import { useLayout } from "../layout/LayoutProvider";
import { TANK_ZOOMS, type TankZoom } from "../layout/layout";
import { useNotify } from "../notify";
import { byKind, capacityLabel, inCellar, kindPlural } from "../tankLayout";
import {
  askBefore,
  type Bar,
  barsOf,
  brewingIn,
  brewName,
  type Drop,
  type DropQuestion,
  dropDays,
  freeNote,
  type Landing,
  landing,
  landingDays,
  laneFor,
  reachFor,
  type Span,
  type Step,
  spanAround,
  stack,
  stayNotes,
  stepsFor,
  timeScale,
  unbookedBar,
  ZOOM_LABELS,
} from "../tankSchedule";
import { useTodayDetent } from "../todayDetent";
import { AskDialog, BrewPanel } from "./BrewPanel";

/**
 * A lane's height and a bar's in it. On a touch screen a bar is tapped with a thumb, often a wet or
 * gloved one, so it is drawn taller there (the critique, 2026-10-06: 22px was too small to hit): 48px,
 * as every touch control is (Tyler, 2026-10-07: "let's try the bars at 48").
 */
const LANES = { fine: { lane: 26, bar: 22 }, touch: { lane: 54, bar: 48 } } as const;
/** The header: the months, then each day's weekday over its date, or each week's Monday. */
const HEAD = 58;
/** The months' row at its top, which the lines between days and weeks start under. */
const MONTH_ROW = 20;
/** Where today rests across the chart, as a share of the days in view: near the left. */
const TODAY_AT = 0.12;
interface Anchor {
  /** A day, and how far into it, to keep in place... */
  day: string;
  frac: number;
  /** ...at this share of the chart's width from its left. */
  at: number;
}
/**
 * The tank schedule (Scott's Jam, 2026-10-03: "the rows will represent tanks, bright tanks,
 * fermenters. The columns will represent days"), on top of Production with the cellar under it (Tyler,
 * 2026-10-05). Each beer's time in a tank is a bar. A slider sets how much time fits across, from a
 * week to a year; the chart itself scrolls back through what was brewed and on as far as anything is
 * planned, loading more as it goes, so it never stops at a horizon.
 *
 * On a desktop a bar drags to another day or tank and its end stretches; anywhere, clicking one opens
 * its brew's tanks, which is how a phone changes them, where dragging would fight scrolling. A brew
 * whose brew day has started opens its brew day instead, which has its tanks too: with the Brew day
 * list gone from the sidebar, this is the way to it (Scott's Jam, 2026-10-08).
 */
export function TankSchedule({
  tanks,
  onPlan,
}: {
  /** Every tank the server has, retired ones too, for the beers they held. */
  tanks: Tank[];
  /** Add a brew, starting on a day clicked and in the fermenter it was clicked in. */
  onPlan: (on: string, tankId?: string) => void;
}) {
  const { layout, update } = useLayout();
  const p = layout.production;
  const set = (patch: Partial<typeof p>) =>
    update((l) => ({ ...l, production: { ...l.production, ...patch } }));
  const now = today();
  const { lane: laneH, bar: barH } = useMedia("(pointer: coarse)") ? LANES.touch : LANES.fine;
  const scroller = useRef<HTMLDivElement>(null);
  const spanText = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState(0);
  const [span, setSpan] = useState<Span>(() => spanAround(now, p.zoom));
  const { data, error, isLoading } = useTankStays(span.from, span.to);
  const navigate = useNavigate();
  const [opened, setOpened] = useState<string | null>(null);
  const [drag, setDrag] = useState<Drop | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  // A drop waiting on a yes: a double batch, or a hidden tank.
  const [asking, setAsking] = useState<{ q: DropQuestion; bar: Bar; steps: Step[] } | null>(null);

  const nameW = width && width < 560 ? 124 : 172;
  const visible = Math.max(1, width - nameW);
  const dw = Math.max(1.5, visible / p.zoom);
  const total = daysApart(span.from, span.to) + 1;
  const trackW = total * dw;
  const x = (on: string) => daysApart(span.from, on) * dw;

  // Kept for the scroll and resize handlers, which outlive a render.
  const geom = useRef({ from: span.from, dw, visible, zoom: p.zoom });
  geom.current = { from: span.from, dw, visible, zoom: p.zoom };
  // Where the chart should be once drawn: today near the left to begin with.
  const anchor = useRef<Anchor | null>({ day: now, frac: 0.5, at: TODAY_AT });
  const extending = useRef(false);
  // Today holds a scroll that would carry the chart past it, where the Today button puts it.
  const detent = useTodayDetent(scroller, () => {
    const g = geom.current;
    return Math.max(0, (daysApart(g.from, now) + 0.5) * g.dw - TODAY_AT * g.visible);
  });

  /** Today, if it is in view, else the middle: what a zoom or a resize keeps in its place. */
  const place = (): Anchor => {
    const el = scroller.current;
    const g = geom.current;
    if (!el) return { day: now, frac: 0.5, at: 0.5 };
    const t = (daysApart(g.from, now) + 0.5) * g.dw - el.scrollLeft;
    if (t >= 0 && t <= g.visible) return { day: now, frac: 0.5, at: t / g.visible };
    const mid = (el.scrollLeft + g.visible / 2) / g.dw;
    return { day: dayPlus(g.from, Math.floor(mid)), frac: mid - Math.floor(mid), at: 0.5 };
  };

  // Names in view for a bar or month scrolled half under the tank names, and the days in view.
  const pin = () => {
    const el = scroller.current;
    if (!el) return;
    const g = geom.current;
    const left = el.scrollLeft;
    for (const label of el.querySelectorAll<HTMLElement>("[data-pin]")) {
      const box = label.parentElement as HTMLElement;
      const want = Math.max(0, left - Number(box.dataset.x));
      const room = Math.max(0, Number(box.dataset.w) - label.offsetWidth - 6);
      label.style.transform = want ? `translateX(${Math.min(want, room)}px)` : "";
      // Only a sliver left in view: no name at all rather than half of one.
      label.style.visibility = want > room ? "hidden" : "";
    }
    if (spanText.current) {
      const first = Math.max(0, Math.floor(left / g.dw));
      const last = Math.max(first, Math.floor((left + g.visible) / g.dw - 0.01));
      spanText.current.textContent = `${showDate(dayPlus(g.from, first))} to ${showDate(dayPlus(g.from, last))}`;
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: watched once; place reads refs
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      setWidth((was) => {
        if (was && w !== was) anchor.current = anchor.current ?? place();
        return w;
      });
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    return () => watch.disconnect();
  }, []);

  // After each drawing: back to the place kept, and the pinned names where they belong.
  useLayoutEffect(() => {
    extending.current = false;
    const el = scroller.current;
    const a = anchor.current;
    if (el && width && a) {
      anchor.current = null;
      el.scrollLeft = Math.max(0, (daysApart(span.from, a.day) + a.frac) * dw - a.at * visible);
      detent.sync();
    }
    pin();
  });

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    detent.check();
    pin();
    if (extending.current) return;
    const g = geom.current;
    const reach = reachFor(g.zoom);
    if (el.scrollLeft < g.visible * 0.75) {
      extending.current = true;
      const left = el.scrollLeft / g.dw;
      anchor.current = {
        day: dayPlus(g.from, Math.floor(left)),
        frac: left - Math.floor(left),
        at: 0,
      };
      setSpan((s) => ({ ...s, from: dayPlus(s.from, -reach) }));
    } else if (el.scrollLeft + el.clientWidth > el.scrollWidth - g.visible * 0.75) {
      extending.current = true;
      setSpan((s) => ({ ...s, to: dayPlus(s.to, reach) }));
    }
  };

  const zoomTo = (zoom: TankZoom) => {
    if (zoom === p.zoom) return;
    const a = place();
    anchor.current = a;
    setSpan((s) => spanAround(a.day, zoom, s));
    set({ zoom });
  };
  const page = (dir: 1 | -1) => {
    detent.gesture();
    scroller.current?.scrollBy({ left: dir * visible * 0.9, behavior: "smooth" });
  };
  const toToday = () => {
    anchor.current = { day: now, frac: 0.5, at: TODAY_AT };
    setSpan((s) => spanAround(now, p.zoom, s));
  };

  const stays = data?.stays ?? [];
  const cellar = inCellar(tanks);
  // A retired tank stays on the chart while a beer it held is in view.
  const retired = tanks.filter((t) => t.retired && stays.some((s) => s.tank.id === t.id));
  const hasBrites = cellar.some((t) => t.kind === "Brite tank");
  // Hidden tanks are the brewery's (Tyler, 2026-10-05), shown greyed out at the foot of their kind.
  const rank = (t: Tank) => (t.hidden ? 2 : t.outOfService ? 1 : 0);
  const groups = byKind([...cellar, ...retired].filter((t) => p.showHidden || !t.hidden)).map(
    (g) => ({ ...g, tanks: [...g.tanks].sort((a, b) => rank(a) - rank(b)) }),
  );
  const loose = (data?.unbooked ?? []).map(unbookedBar);
  const looseLanes = stack(loose);
  const outOfService = (id: string) => tanks.find((t) => t.id === id)?.outOfService ?? false;

  const press = useRef<{
    id: number;
    x: number;
    y: number;
    bar: Bar;
    mode: Drop["mode"];
    moved: boolean;
  } | null>(null);
  const swallowClick = useRef(false);
  const run = useChange(async (steps: Step[], key) => {
    for (const [i, s] of steps.entries()) {
      // One key per request, all from the one kept for this drop, so a retry repeats none of them.
      const k = steps.length === 1 ? key : `${key}.${i}`;
      if (s.kind === "brewDay") {
        const patch = { brewedOn: s.on, ...(s.keepBookings ? { moveBookings: false } : {}) };
        await send.saveBrew({ id: s.brewId, patch }, k);
      } else if (s.kind === "change") await send.changeStay({ id: s.stayId, patch: s.patch }, k);
      else await send.addStay({ brewId: s.brewId, tankId: s.tankId, inOn: s.inOn }, k);
    }
  });
  const notify = useNotify();
  // One tap hides a tank for everyone, so it says what it did and where to undo it (the critique,
  // 2026-10-06).
  const hide = useChange(
    (t: { id: string; hidden: boolean }, key) =>
      send.saveTank({ id: t.id, patch: { hidden: t.hidden } }, key),
    {
      onSuccess: (t) =>
        notify(
          t.hidden
            ? {
                title: `${t.name} hidden from the schedule`,
                body: "For everyone at the brewery. Hidden tanks, in the toolbar, shows it again.",
              }
            : `${t.name} is back on the schedule`,
        ),
    },
  );

  const draggable = (bar: Bar, mode: Drop["mode"]) => {
    if (bar.tone === "done" || bar.stays.length > 1) return false;
    if (mode === "end") return bar.stays.length === 1 && bar.to !== null;
    // Its start stretches while it is a booking; a beer in a tank went in on its day.
    if (mode === "start") return bar.stays.length === 1 && bar.stays[0]?.state === "booked";
    return bar.stays[0]?.state !== "in";
  };

  const down = (e: PointerEvent<HTMLButtonElement>, bar: Bar) => {
    swallowClick.current = false;
    if (e.pointerType !== "mouse" || e.button !== 0 || run.isPending) return;
    const handle = (e.target as HTMLElement).dataset;
    const mode =
      handle.end !== undefined ? "end" : handle.start !== undefined ? "start" : ("move" as const);
    if (!draggable(bar, mode)) return;
    press.current = { id: e.pointerId, x: e.clientX, y: e.clientY, bar, mode, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  /** Where the pointer is, as a drop: the days it has come, and the row it is over. */
  const dropAt = (e: PointerEvent<HTMLButtonElement>, pr: NonNullable<typeof press.current>) => {
    const over = document
      .elementFromPoint(e.clientX, e.clientY)
      ?.closest<HTMLElement>("[data-row]");
    return {
      bar: pr.bar,
      mode: pr.mode,
      days: Math.round((e.clientX - pr.x) / dw),
      row: pr.mode !== "move" ? pr.bar.tankId : over ? over.dataset.row || null : undefined,
    };
  };
  const move = (e: PointerEvent<HTMLButtonElement>) => {
    const pr = press.current;
    if (!pr || pr.id !== e.pointerId) return;
    if (!pr.moved && Math.hypot(e.clientX - pr.x, e.clientY - pr.y) < 5) return;
    pr.moved = true;
    const at = dropAt(e, pr);
    setDrag((d) =>
      d && d.days === at.days && d.row === at.row && d.bar.key === at.bar.key ? d : at,
    );
  };
  const up = (e: PointerEvent<HTMLButtonElement>) => {
    const pr = press.current;
    if (!pr || pr.id !== e.pointerId) return;
    press.current = null;
    if (!pr.moved) return;
    swallowClick.current = true;
    const dropped = dropAt(e, pr);
    const steps = stepsFor(dropped, { outOfService });
    if (typeof steps === "string" || steps.length === 0) {
      setRefused(typeof steps === "string" ? steps : null);
      setDrag(null);
      return;
    }
    setRefused(null);
    // The bar stays where it was dropped until the chart is drawn again from the server, or, asked
    // about first, until it is put back.
    setDrag(dropped);
    const q = askBefore(dropped, tanks, stays);
    if (q) setAsking({ q, bar: dropped.bar, steps });
    else run.mutate(steps, { onSettled: () => setDrag(null) });
  };
  const answer = (yes: boolean) => {
    const steps = asking?.steps;
    setAsking(null);
    if (yes && steps) run.mutate(steps, { onSettled: () => setDrag(null) });
    else setDrag(null);
  };
  const cancel = () => {
    press.current = null;
    setDrag(null);
  };
  const dragging = drag !== null && !run.isPending;
  useEffect(() => {
    if (!dragging) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      press.current = null;
      setDrag(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [dragging]);

  const scale = useMemo(() => timeScale(span.from, total, dw), [span.from, total, dw]);

  const weekends: CSSProperties =
    dw >= 6
      ? {
          backgroundImage: `repeating-linear-gradient(90deg, transparent 0 ${5 * dw}px, var(--weekend) ${5 * dw}px ${7 * dw}px)`,
        }
      : {};

  /** Where a stay from one day to another is drawn: from the middle of its first day to its last's. */
  const extent = (from: string, to: string | null, stretch = 0) => {
    const start = x(from);
    const days = to === null ? null : daysApart(from, to);
    const left = days === 0 ? start + dw * 0.2 : start + dw / 2;
    const wide =
      days === null
        ? Math.max(dw, trackW - left)
        : Math.max(dw * 0.6, days * dw + stretch, days === 0 ? 0 : dw * 0.6);
    return { days, left, wide };
  };

  // A bar dragged over another tank's row lands in it there, at the days it would take in that tank.
  const over = drag && typeof drag.row === "string" ? tanks.find((t) => t.id === drag.row) : null;
  const landed: Landing | null = drag && over ? landing(drag, over, stays) : null;
  // Said while it is dragged, before anything is asked: a double batch it would make there.
  const asks = drag && !asking ? askBefore(drag, tanks, stays) : null;
  const combineNote = asks?.combines.length
    ? `, a double batch with ${asks.combines.map(brewName).join(" and ")}`
    : "";

  const barView = (bar: Bar, tank: Tank | null) => {
    const dragged = drag?.bar.key === bar.key ? drag : null;
    // Gone over to another row, it is drawn faded where it was.
    const away = dragged !== null && landed !== null;
    const shift = dragged?.mode === "move" && !away ? dragged.days * dw : 0;
    const stretch = dragged?.mode === "end" ? dragged.days * dw : 0;
    // Its start dragged: drawn from the new first day to the same last one.
    const from = dragged?.mode === "start" ? dayPlus(bar.from, dragged.days) : bar.from;
    const { days, left, wide } = extent(from, bar.to, stretch);
    const top = 7 + bar.lane * laneH;
    const where = tank?.name ?? "No tank yet";
    const when = `${showDate(bar.from)} to ${bar.to ? showDate(bar.to) : "no day out set"}`;
    // The clash in the words its warnings use: the tank full or scheduled for another beer then.
    const clashes = bar.clash ? [...new Set(bar.stays.flatMap(stayNotes))] : [];
    const issues = [
      ...clashes,
      bar.overdue ? "Overdue" : null,
      bar.noBrite ? "No brite tank booked" : null,
    ].filter(Boolean);
    // One of a recurring brew, marked with the repeat icon (Tyler, 2026-10-05).
    const recurring = bar.brews.some((b) => b.seriesId !== null);
    // A brew whose brew day has started is marked with brew day's flame, and opens it.
    const brewing = brewingIn(bar);
    const marks = [brewing ? "brew day in progress" : null, recurring ? "recurring" : null].filter(
      Boolean,
    );
    const title = `${bar.label}${marks.length ? ` (${marks.join(", ")})` : ""}: ${where}, ${when}${issues.length ? `. ${issues.join(". ")}` : ""}`;
    const tone = bar.clash ? "clash" : bar.tone;
    const resizable = draggable(bar, "end");
    const startable = draggable(bar, "start");
    return (
      <div key={bar.key}>
        <button
          type="button"
          className={`ts-bar ${tone} ${days === null ? "open-ended" : ""} ${dragged ? "dragging" : ""} ${away ? "left-behind" : ""} ${draggable(bar, "move") ? "movable" : ""}`}
          style={{ left: left + shift, width: wide, top }}
          data-x={left + shift}
          data-w={wide}
          title={title}
          aria-label={title}
          onPointerDown={(e) => down(e, bar)}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={cancel}
          onClick={() => {
            if (swallowClick.current) {
              swallowClick.current = false;
              return;
            }
            // A brew day under way opens itself (Scott's Jam, 2026-10-08: "active brew days should be
            // accessible by clicking the beer in the production tab"); its page has its tanks too.
            if (brewing) {
              navigate({ to: "/brews/$brewId", params: { brewId: brewing.id } });
              return;
            }
            const brew = bar.brews[0];
            if (brew) setOpened(brew.id);
          }}
        >
          {wide >= 40 ? (
            <span className="ts-bar-label" data-pin>
              {brewing ? <Flame size={11} className="ts-bar-series" aria-hidden /> : null}
              {recurring ? <RepeatIcon size={11} className="ts-bar-series" aria-hidden /> : null}
              {bar.label}
            </span>
          ) : null}
          {startable ? <span className="ts-bar-start" data-start aria-hidden /> : null}
          {resizable ? <span className="ts-bar-end" data-end aria-hidden /> : null}
        </button>
        {bar.noBrite && !dragged ? (
          <span
            className="ts-flag"
            style={{ left: left + wide - 9, top: top - 7 }}
            title="No brite tank booked after it"
            aria-hidden
          >
            !
          </span>
        ) : null}
        {dragged && !away ? (
          <span className="ts-drag-note" style={{ left: left + shift, top: top - 20 }}>
            {bar.stays.length === 0 && dragged.mode === "move" ? (
              // Along No tank yet it goes back where it was; only a tank books it.
              "Drop it on a tank to book it"
            ) : (
              <>
                {dropDays(dragged)}
                {dragged.row && dragged.row !== bar.tankId
                  ? `, ${tanks.find((t) => t.id === dragged.row)?.name ?? ""}`
                  : ""}
                {combineNote}
              </>
            )}
          </span>
        ) : null}
      </div>
    );
  };

  /** The dragged bar where it would land in the tank under the pointer, with its days over it. */
  const landingView = (
    bar: Bar,
    l: Landing,
    tank: Tank,
    lane: number,
    /** Where its row ends, when it spills over the row below. */
    spillFrom: number | null,
  ) => {
    const { days, left, wide } = extent(l.from, l.to);
    const top = 7 + lane * laneH;
    const below = spillFrom !== null;
    return (
      <>
        {below ? (
          <div
            className="ts-spill"
            style={{ top: spillFrom, height: top + barH + 24 - spillFrom, width: trackW }}
            aria-hidden
          />
        ) : null}
        <div
          className={`ts-bar ts-landing ${l.clash ? "clash" : "planned"} ${days === null ? "open-ended" : ""} ${below ? "spilled" : ""}`}
          style={{ left, width: wide, top }}
          aria-hidden
        >
          {wide >= 40 ? <span className="ts-bar-label">{bar.label}</span> : null}
        </div>
        <span className="ts-drag-note" style={{ left, top: below ? top + barH + 3 : top - 20 }}>
          {landingDays(l)}, {tank.name}
          {tank.hidden ? " (hidden)" : ""}
          {l.clash ? `: ${l.clash} then` : combineNote}
        </span>
      </>
    );
  };

  const trackClick = (e: MouseEvent<HTMLDivElement>, tank: Tank | null) => {
    if (e.target !== e.currentTarget) return;
    const on = dayPlus(span.from, Math.floor(e.nativeEvent.offsetX / dw));
    if (on < now) return;
    if (tank && (tank.kind !== "Fermenter" || tank.outOfService || tank.retired)) return;
    onPlan(on, tank?.id);
  };

  const row = (key: string, tank: Tank | null, bars: Bar[], lanes: number) => {
    const isHidden = tank?.hidden ?? false;
    const note = tank ? freeNote(tank, now) : null;
    // A bar dragged over this row lands in the first lane free for its days.
    const lands = landed && drag && tank && drag.row === tank.id ? landed : null;
    const landLane = lands ? laneFor(lands, bars, lanes) : 0;
    // In a new lane under the others, it spills over the row below without moving it, so the rows
    // stay where the pointer found them; its days go under it, clear of the bars above.
    const spills = lands !== null && landLane === lanes;
    const height = Math.max(44, 14 + lanes * laneH);
    const target =
      drag &&
      drag.mode === "move" &&
      drag.row !== drag.bar.tankId &&
      drag.row === (tank?.id ?? null);
    const plans =
      tank === null || (tank.kind === "Fermenter" && !tank.outOfService && !tank.retired);
    return (
      <div
        key={key}
        className={`ts-row ${tank?.outOfService ? "ghost" : ""} ${isHidden ? "hidden-tank" : ""} ${target ? (tank?.outOfService ? "no-drop" : "drop-target") : ""}`}
        data-row={tank?.id ?? ""}
      >
        <div className="ts-name" style={{ width: nameW, height }}>
          {tank ? (
            <>
              <span className="ts-name-line">
                <span className="ts-tank" title={tank.outOfService ? "Out of service" : undefined}>
                  {tank.name}
                </span>
                <span className="ts-cap">{capacityLabel(tank)}</span>
                {tank.retired ? null : (
                  <button
                    type="button"
                    className="ts-eye"
                    aria-label={isHidden ? `Show ${tank.name}` : `Hide ${tank.name}`}
                    title={
                      isHidden
                        ? `Show ${tank.name} on the schedule again, for everyone`
                        : `Hide ${tank.name} from the schedule, for everyone at the brewery`
                    }
                    disabled={hide.isPending}
                    onClick={() => hide.mutate({ id: tank.id, hidden: !isHidden })}
                  >
                    {isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                )}
              </span>
              <span className={`ts-free ${note?.free ? "free" : ""}`}>
                {tank.retired ? "Retired" : note?.text}
              </span>
            </>
          ) : (
            <>
              <span className="ts-name-line">
                <span className="ts-tank">No tank yet</span>
              </span>
              <span className="ts-free">
                {bars.length} {bars.length === 1 ? "brew" : "brews"}
              </span>
            </>
          )}
        </div>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: a shortcut; Add a brew is in the title bar */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: as above */}
        <div
          className={`ts-track ${plans ? "plans" : ""}`}
          style={{ width: trackW, height, ...(tank?.outOfService ? {} : weekends) }}
          title={plans ? `Click a day to add a brew${tank ? ` in ${tank.name}` : ""}` : undefined}
          onClick={plans ? (e) => trackClick(e, tank) : undefined}
        >
          {bars.map((b) => barView(b, tank))}
          {lands && drag && tank
            ? landingView(drag.bar, lands, tank, landLane, spills ? height : null)
            : null}
        </div>
      </div>
    );
  };

  return (
    <section className="tank-schedule" aria-label="Tank schedule">
      <div className="ts-controls">
        <h2 className="brew-heading">Tank schedule</h2>
        <div className="ts-nav">
          <button
            type="button"
            className="button small"
            aria-label="Back"
            title="Back"
            onClick={() => page(-1)}
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            className="button small"
            aria-label="Forward"
            title="Forward"
            onClick={() => page(1)}
          >
            <ChevronRight size={16} />
          </button>
          <button type="button" className="button small" onClick={toToday}>
            Today
          </button>
          <span className="ts-span" ref={spanText} />
        </div>
        {/* How much time fits across (Tyler, 2026-10-05): a week at its - end to a year at its +. */}
        <ZoomSlider
          stops={TANK_ZOOMS}
          value={p.zoom}
          onZoom={zoomTo}
          labelOf={(z) => ZOOM_LABELS[z]}
          name="Time across the schedule"
          less="Less time across"
          more="More time across"
        />
      </div>
      <ErrorNote error={error ?? run.error ?? hide.error} />
      {refused ? (
        <p className="stay-warning" role="status">
          <TriangleAlert size={15} /> {refused}.
        </p>
      ) : null}
      {/* A bar reached with the keyboard is scrolled clear of the tank names, which stay put over the
          left of the chart (the re-critique, 2026-10-07: a third of the bars on a phone sat under them). */}
      <div
        className="ts-scroll"
        ref={scroller}
        onScroll={onScroll}
        style={{ scrollPaddingLeft: nameW }}
      >
        <div className="ts-grid" style={{ width: nameW + trackW }}>
          <div className="ts-row ts-head">
            <div className="ts-name" style={{ width: nameW, height: HEAD }}>
              <span className="ts-free">Tank</span>
            </div>
            <div className="ts-track" style={{ width: trackW, height: HEAD }}>
              {scale.months.map((m) => (
                <div
                  key={m.on}
                  className="ts-month"
                  style={{ left: m.x, width: m.w }}
                  data-x={m.x}
                  data-w={m.w}
                >
                  <span data-pin>{m.label}</span>
                </div>
              ))}
              {scale.days.map((d) => (
                <div
                  key={d.on}
                  className={`ts-day ${d.on === now ? "today" : ""}`}
                  style={{
                    left: d.x,
                    width: Math.max(dw, 18),
                    marginLeft: dw < 18 ? (dw - 18) / 2 : 0,
                  }}
                >
                  <span className="ts-weekday">{d.weekday}</span>
                  <span>{d.date}</span>
                </div>
              ))}
              {scale.weeks.map((w) =>
                w.label === null ? null : (
                  <div key={w.on} className="ts-week" style={{ left: w.x }}>
                    {w.label}
                  </div>
                ),
              )}
            </div>
          </div>
          {loose.length ? row("loose", null, loose, looseLanes) : null}
          {groups.map((g) => {
            const folded = p.folded[g.kind] ?? false;
            return (
              <div key={g.kind} className="ts-group">
                <div className="ts-row ts-section">
                  <div className="ts-name" style={{ width: nameW }}>
                    <button
                      type="button"
                      className="ts-fold"
                      aria-expanded={!folded}
                      onClick={() => set({ folded: { ...p.folded, [g.kind]: !folded } })}
                    >
                      <ChevronDown size={14} className="caret" />
                      {kindPlural(g.kind)} <span className="faint">{g.tanks.length}</span>
                    </button>
                  </div>
                  <div className="ts-track" style={{ width: trackW }} />
                </div>
                {folded
                  ? null
                  : g.tanks.map((t) => {
                      const bars = barsOf(
                        stays.filter((s) => s.tank.id === t.id),
                        { hasBrites, all: stays },
                      );
                      return row(t.id, t, bars, stack(bars));
                    })}
              </div>
            );
          })}
          {/* Over the rows' shading and under their bars, as the today line is. */}
          <div
            className="ts-lines"
            style={{ left: nameW, top: MONTH_ROW, width: trackW }}
            aria-hidden
          >
            {scale.lines.map((l) => (
              <i
                key={l.on}
                className={l.month ? "month" : undefined}
                style={{ left: Math.round(l.x) }}
              />
            ))}
          </div>
          {daysApart(span.from, now) >= 0 && now <= span.to ? (
            <div
              className="ts-today"
              style={{ left: nameW + (daysApart(span.from, now) + 0.5) * dw - 1, top: HEAD }}
              aria-hidden
            />
          ) : null}
        </div>
      </div>
      {isLoading ? <p className="muted">Loading</p> : null}
      <div className="ts-legend">
        <span>
          <i className="sw in" />
          In the tank
        </span>
        <span>
          <i className="sw planned" />
          Planned
        </span>
        <span>
          <i className="sw done" />
          Out of the tank
        </span>
        <span>
          <i className="sw clash" />
          Two beers at once, or too much
        </span>
        {hasBrites ? (
          <span>
            <i className="sw warn-dot" />
            No brite tank booked
          </span>
        ) : null}
        {cellar.some((t) => t.outOfService) ? (
          <span>
            <i className="sw ghost" />
            Out of service
          </span>
        ) : null}
      </div>
      {opened ? <BrewPanel brewId={opened} onClose={() => setOpened(null)} /> : null}
      {asking ? (
        <AskDialog
          q={asking.q}
          bar={asking.bar}
          onYes={() => answer(true)}
          onNo={() => answer(false)}
        />
      ) : null}
    </section>
  );
}
